import type {
  PartnerAnwender,
  PartnerAnwenderAendern,
  PartnerAnwenderAnlegen,
  PartnerGeraet,
  PartnerKennwort,
  PartnerKonten,
  PartnerKontoAendern,
  PartnerKontoAnlegen,
  PartnerKopplungscode,
  PartnerKunde,
  PartnerKundeAendern,
  PartnerKundeAenderung,
  PartnerProfil,
  PartnerProfilAendern,
  PartnerPush,
  PartnerSitzung,
  PartnerZugangslink,
} from '@nac2s/shared'
import {
  type EinrichtungCodeAntwort,
  type EinrichtungWartenAntwort,
  type EinrichtungZustand,
  einrichtungCodeAntwortSchema,
  einrichtungWartenAntwortSchema,
  einrichtungZustandSchema,
  type Kopplungsstand,
  kopplungsstandSchema,
  type Lizenz,
  type LizenzKaufen,
  type LizenzVorschau,
  lizenzSchema,
  lizenzVorschauSchema,
  type PartnerEditionen,
  partnerAnwenderListeSchema,
  partnerAnwenderSchema,
  partnerEditionenSchema,
  partnerGeraetListeSchema,
  partnerKennwortSchema,
  partnerKontenSchema,
  partnerKopplungscodeSchema,
  partnerKundeAenderungenSchema,
  partnerKundenSchema,
  partnerProfilSchema,
  partnerPushSchema,
  partnerSitzungSchema,
  partnerZugangslinkSchema,
} from '@nac2s/shared'
import { apiFetch } from './client.js'

/**
 * Endpunkte der Partner-Oberfläche.
 *
 * Die Antworten werden **hier geprüft**, nicht nur getypt. Ein Typ ist beim
 * Übersetzen weg; was der Server tatsächlich schickt, weiß er nicht. Weicht
 * die Antwort vom Vertrag ab, ist der Fehler an dieser Stelle sichtbar — und
 * nicht drei Komponenten weiter als `undefined` in einer Tabellenzelle.
 */

export async function ladeSitzung(): Promise<PartnerSitzung> {
  return partnerSitzungSchema.parse(await apiFetch('/partner/api/sitzung'))
}

export async function ladeKunden(): Promise<PartnerKunde[]> {
  const antwort = await apiFetch('/partner/api/kunden')
  return partnerKundenSchema.parse(antwort).kunden
}

export async function ladeAnwender(host: string): Promise<PartnerAnwender[]> {
  const antwort = await apiFetch(`/partner/api/kunden/${encodeURIComponent(host)}/anwender`)
  return partnerAnwenderListeSchema.parse(antwort).anwender
}

/**
 * Vergibt die Selbstverwaltung an einen Anwender des Kunden — oder nimmt sie.
 *
 * Wie weit sie reicht, entscheidet die Stufe am Kunden. Hier geht es nur um
 * die Person (`Projektstruktur.md` §3.1).
 */
export async function anwenderAendern(
  host: string,
  id: string,
  aenderung: PartnerAnwenderAendern,
): Promise<void> {
  await apiFetch(
    `/partner/api/kunden/${encodeURIComponent(host)}/anwender/${encodeURIComponent(id)}`,
    { method: 'PATCH', body: JSON.stringify(aenderung) },
  )
}

/** Legt einen Anwender an; den Kopplungscode holt man danach getrennt. */
export async function anwenderAnlegen(
  host: string,
  eingabe: PartnerAnwenderAnlegen,
): Promise<PartnerAnwender> {
  const antwort = await apiFetch(`/partner/api/kunden/${encodeURIComponent(host)}/anwender`, {
    method: 'POST',
    body: JSON.stringify(eingabe),
  })
  return partnerAnwenderSchema.parse((antwort as { anwender: unknown }).anwender)
}

/**
 * Zugangslink — **einmalig** in der Antwort (ADR-0004 Nachtrag 5).
 *
 * Damit setzt der Anwender sein Passwort und meldet sich danach von jedem
 * Gerät an. Es gibt keinen Mailversand: Der Link geht von Hand weiter.
 */
export async function zugangslinkAusstellen(host: string, id: string): Promise<PartnerZugangslink> {
  return partnerZugangslinkSchema.parse(
    await apiFetch(
      `/partner/api/kunden/${encodeURIComponent(host)}/anwender/${encodeURIComponent(id)}/zugangslink`,
      { method: 'POST' },
    ),
  )
}

/** Kopplungscode — **einmalig** in der Antwort. */
export async function kopplungscodeAusgeben(
  host: string,
  id: string,
): Promise<PartnerKopplungscode> {
  return partnerKopplungscodeSchema.parse(
    await apiFetch(
      `/partner/api/kunden/${encodeURIComponent(host)}/anwender/${encodeURIComponent(id)}/kopplungscode`,
      { method: 'POST' },
    ),
  )
}

/** Push-Adresse des Kunden — enthaelt sein Geheimnis, nur fuer den sipgate-Administrator. */
export async function ladePush(host: string): Promise<PartnerPush> {
  return partnerPushSchema.parse(
    await apiFetch(`/partner/api/kunden/${encodeURIComponent(host)}/push`),
  )
}

export async function pushErzeugen(host: string): Promise<PartnerPush> {
  return partnerPushSchema.parse(
    await apiFetch(`/partner/api/kunden/${encodeURIComponent(host)}/push`, { method: 'POST' }),
  )
}

export async function ladeGeraete(host: string): Promise<PartnerGeraet[]> {
  const antwort = await apiFetch(`/partner/api/kunden/${encodeURIComponent(host)}/geraete`)
  return partnerGeraetListeSchema.parse(antwort).geraete
}

/**
 * Anmeldung.
 *
 * Better Auth bedient den Endpunkt selbst und setzt das Sitzungscookie. Der
 * Rumpf der Antwort interessiert nicht — die Oberfläche fragt danach die
 * Sitzung ab, und erst deren Antwort entscheidet, ob das Konto auch ein
 * Partner-Konto ist. Ein Konto mit gültigem Passwort ist noch keines.
 */
export async function anmelden(email: string, passwort: string): Promise<void> {
  await apiFetch('/partner/auth/sign-in/email', {
    method: 'POST',
    body: JSON.stringify({ email, password: passwort }),
  })
}

export async function abmelden(): Promise<void> {
  await apiFetch('/partner/auth/sign-out', { method: 'POST', body: '{}' })
}

// --- Konten des Partners (`Projektstruktur.md` §3.0) -----------------------

/**
 * Konten **und** den Rollenkatalog in einem Zug.
 *
 * Zwei Abfragen fuer eine Seite waeren zwei Ladezustaende, von denen einer
 * immer zuerst da ist — und eine Auswahl, die kurz nicht erklaeren kann, was
 * sie vergibt. Der Katalog ist drei Zeilen gross und gehoert hier dazu.
 */
export async function ladeKonten(): Promise<PartnerKonten> {
  return partnerKontenSchema.parse(await apiFetch('/partner/api/konten'))
}

/**
 * Legt ein Konto an und liefert das Kennwort — **einmalig**.
 *
 * Es steht in keiner weiteren Antwort. Wer es nicht weitergibt, muss ein
 * neues erzeugen; ein Kennwort, das sich später noch einmal abrufen lässt,
 * ist keines.
 */
export async function kontoAnlegen(eingabe: PartnerKontoAnlegen): Promise<PartnerKennwort> {
  return partnerKennwortSchema.parse(
    await apiFetch('/partner/api/konten', { method: 'POST', body: JSON.stringify(eingabe) }),
  )
}

export async function kontoAendern(id: string, aenderung: PartnerKontoAendern): Promise<void> {
  await apiFetch(`/partner/api/konten/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(aenderung),
  })
}

export async function kennwortZuruecksetzen(id: string): Promise<PartnerKennwort> {
  return partnerKennwortSchema.parse(
    await apiFetch(`/partner/api/konten/${encodeURIComponent(id)}/kennwort`, {
      method: 'POST',
      body: '{}',
    }),
  )
}

// --- Stellschrauben eines Kunden (`Projektstruktur.md` §8) -----------------

export async function kundeAendern(host: string, aenderung: PartnerKundeAendern): Promise<void> {
  await apiFetch(`/partner/api/kunden/${encodeURIComponent(host)}`, {
    method: 'PATCH',
    body: JSON.stringify(aenderung),
  })
}

export async function ladeAenderungen(host: string): Promise<PartnerKundeAenderung[]> {
  const antwort = await apiFetch(`/partner/api/kunden/${encodeURIComponent(host)}/aenderungen`)
  return partnerKundeAenderungenSchema.parse(antwort).aenderungen
}

/** Unternehmens- und Supportdaten des Partners — die Fußzeile der Kunden. */
export async function ladeProfil(): Promise<PartnerProfil> {
  return partnerProfilSchema.parse(await apiFetch('/partner/api/profil'))
}

export async function speichereProfil(eingabe: PartnerProfilAendern): Promise<PartnerProfil> {
  return partnerProfilSchema.parse(
    await apiFetch('/partner/api/profil', { method: 'PUT', body: JSON.stringify(eingabe) }),
  )
}

/** Editionskatalog dieses Partners — was netable ihm zu verkaufen gibt (ADR-0014). */
export async function ladeEditionen(): Promise<PartnerEditionen> {
  return partnerEditionenSchema.parse(await apiFetch('/partner/api/editionen'))
}

/**
 * Vorschau eines Kaufs — der Server rechnet Preis, Testzeitraum, Zahlungsbeginn
 * und Laufzeitende und sagt, ob der Kauf angesichts einer laufenden Lizenz geht.
 */
export async function lizenzVorschau(host: string, eingabe: LizenzKaufen): Promise<LizenzVorschau> {
  const antwort = await apiFetch<{ vorschau: unknown }>(
    `/partner/api/kunden/${host}/lizenz/vorschau`,
    { method: 'POST', body: JSON.stringify(eingabe) },
  )
  return lizenzVorschauSchema.parse(antwort.vorschau)
}

/** Edition kaufen oder wechseln — Laufzeit und Mindestmenge (ADR-0015). */
export async function lizenzKaufen(host: string, eingabe: LizenzKaufen): Promise<Lizenz> {
  const antwort = await apiFetch<{ lizenz: unknown }>(`/partner/api/kunden/${host}/lizenz`, {
    method: 'POST',
    body: JSON.stringify(eingabe),
  })
  return lizenzSchema.parse(antwort.lizenz)
}

// ---------------------------------------------------------------------------
// Einrichtungsassistent und Anbindung an netable (ADR-0020)
// ---------------------------------------------------------------------------

export async function ladeEinrichtung(): Promise<EinrichtungZustand> {
  return einrichtungZustandSchema.parse(await apiFetch('/partner/einrichtung/zustand'))
}

/** Schritt 1: Code von netable hinterlegen — die Verwaltung holt ihn ab. */
export async function einrichtungCode(code: string): Promise<EinrichtungCodeAntwort> {
  return einrichtungCodeAntwortSchema.parse(
    await apiFetch('/partner/einrichtung/code', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),
  )
}

/** Schritt 2: Hat die Verwaltung schon gekoppelt? */
export async function einrichtungWarten(
  einrichtungstoken: string,
): Promise<EinrichtungWartenAntwort> {
  return einrichtungWartenAntwortSchema.parse(
    await apiFetch('/partner/einrichtung/warten', {
      method: 'POST',
      body: JSON.stringify({ einrichtungstoken }),
    }),
  )
}

export async function einrichtungKonto(eingabe: {
  einrichtungstoken: string
  email: string
  anzeigename: string
  passwort: string
}): Promise<void> {
  await apiFetch('/partner/einrichtung/konto', { method: 'POST', body: JSON.stringify(eingabe) })
}

/** Bestehender Stack: Code hinterlegen, die Verwaltung koppelt dann neu — braucht `konten.verwalten`. */
export async function kopplungCode(code: string): Promise<{ codeBis: string }> {
  return (await apiFetch('/partner/api/einrichtung/code', {
    method: 'POST',
    body: JSON.stringify({ code }),
  })) as { codeBis: string }
}

export async function ladeKopplung(): Promise<Kopplungsstand> {
  return kopplungsstandSchema.parse(await apiFetch('/partner/api/einrichtung/stand'))
}
